import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';

const fmt = (n) => (n || 0).toLocaleString('en-IN');

const STATUS_FILTERS = ['All', 'Pending', 'Success', 'Failed'];

const statusStyle = (status) => {
  if (status === 'Success') return { bg: 'rgba(34,197,94,0.12)', color: '#22c55e', icon: 'check_circle' };
  if (status === 'Failed')  return { bg: 'rgba(239,68,68,0.12)',  color: '#f87171',  icon: 'cancel' };
  return                          { bg: 'rgba(234,179,8,0.12)',   color: '#eab308',  icon: 'pending' };
};

const VendorPayouts = () => {
  const [payouts, setPayouts]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [activeFilter, setFilter] = useState('All');
  const [noteModal, setNoteModal] = useState(null); // { id, action }
  const [note, setNote]           = useState('');
  const [processing, setProcessing] = useState(false);
  const [toast, setToast]         = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchPayouts = useCallback(async () => {
    try {
      const res = await api.get('/admin/payout-requests');
      console.log('Fetched payouts:', res.data.data);
      setPayouts(res.data.data || []);
    } catch (err) {
      console.error('Payout fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPayouts(); }, [fetchPayouts]);

  const handleAction = async () => {
    if (!noteModal) return;

    if (noteModal.action === 'Success') {
      const confirmed = window.confirm("Are you sure you want to approve this payout request? This will automatically initiate a bank transfer via Razorpay!");
      if (!confirmed) return;
    }

    setProcessing(true);
    try {
      await api.put(`/admin/payout-requests/${noteModal.id}`, {
        status: noteModal.action,
        note,
      });
      showToast(`Payout marked as ${noteModal.action}!`);
      setNoteModal(null);
      setNote('');
      fetchPayouts();
    } catch (err) {
      showToast(err?.response?.data?.message || 'Action failed', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const filtered = activeFilter === 'All' ? payouts : payouts.filter(p => p.status === activeFilter);

  // Summary counts
  const counts = {
    All:     payouts.length,
    Pending: payouts.filter(p => p.status === 'Pending').length,
    Success: payouts.filter(p => p.status === 'Success').length,
    Failed:  payouts.filter(p => p.status === 'Failed').length,
  };
  const totalPending = payouts.filter(p => p.status === 'Pending').reduce((s, p) => s + p.amount, 0);
  const totalPaid    = payouts.filter(p => p.status === 'Success').reduce((s, p) => s + p.amount, 0);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full border-4 border-[#2A2858]/20 border-t-[#2A2858] animate-spin mx-auto mb-4" />
          <p className="text-[#2A2858] font-bold">Loading Payout Requests...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 bg-[#FAF9F6] min-h-screen">
      {/* Toast */}
      {toast && (
        <div className="fixed top-6 right-6 z-50 px-5 py-3 rounded-xl font-bold text-sm shadow-xl"
          style={{ background: toast.type === 'error' ? '#f87171' : '#22c55e', color: '#fff' }}>
          {toast.msg}
        </div>
      )}

      {/* Note / Action Modal */}
      {noteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}>
          <div className="bg-white rounded-3xl p-8 shadow-2xl w-full max-w-md mx-4">
            <h3 className="text-xl font-bold text-stone-800 mb-1">
              Mark as <span style={{ color: noteModal.action === 'Success' ? '#22c55e' : '#f87171' }}>{noteModal.action}</span>
            </h3>
            <p className="text-sm text-stone-400 mb-5">Add an optional note for the vendor.</p>
            <textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              rows={3}
              placeholder="e.g. Payment sent via NEFT (optional)"
              className="w-full border border-stone-200 rounded-xl p-3 text-sm text-stone-700 outline-none resize-none mb-5"
            />
            <div className="flex gap-3">
              <button
                onClick={() => { setNoteModal(null); setNote(''); }}
                className="flex-1 py-3 rounded-xl border border-stone-200 text-stone-500 font-bold text-sm hover:bg-stone-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAction}
                disabled={processing}
                className="flex-1 py-3 rounded-xl font-bold text-sm text-white transition-all"
                style={{ background: noteModal.action === 'Success' ? '#22c55e' : '#f87171' }}
              >
                {processing ? 'Processing...' : `Confirm ${noteModal.action}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-serif font-bold text-[#2A2858]">Payout Requests</h1>
          <p className="text-stone-500 mt-1 text-sm">Review and process vendor and consultation acarya payout requests</p>
        </div>
        <div className="flex gap-4">
          <div className="bg-[#2A2858]/5 border border-[#2A2858]/10 rounded-2xl px-5 py-3 text-center">
            <div className="text-xs font-black uppercase tracking-wider text-[#2A2858]/60 mb-1">Pending Amount</div>
            <div className="text-xl font-bold text-[#2A2858]">₹{fmt(totalPending)}</div>
          </div>
          <div className="bg-green-50 border border-green-100 rounded-2xl px-5 py-3 text-center">
            <div className="text-xs font-black uppercase tracking-wider text-green-600 mb-1">Total Paid Out</div>
            <div className="text-xl font-bold text-green-700">₹{fmt(totalPaid)}</div>
          </div>
        </div>
      </div>

      {/* Status Filter Tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {STATUS_FILTERS.map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className="px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all"
            style={{
              background: activeFilter === f ? '#2A2858' : 'white',
              color: activeFilter === f ? 'white' : '#78716c',
              border: '1px solid',
              borderColor: activeFilter === f ? '#2A2858' : '#e7e5e4',
            }}
          >
            {f} <span className="ml-1 opacity-60">({counts[f]})</span>
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-3xl border border-stone-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="text-xs uppercase font-bold text-stone-400 bg-stone-50/50 border-b border-stone-100">
                <th className="px-6 py-4">Request ID</th>
                <th className="px-6 py-4">Requester</th>
                <th className="px-6 py-4">Type</th>
                <th className="px-6 py-4">Bank Profile Details</th>
                <th className="px-6 py-4">Date</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Admin Note</th>
                <th className="px-6 py-4 text-right">Amount</th>
                <th className="px-6 py-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-50">
              {filtered.length > 0 ? filtered.map(p => {
                const s = statusStyle(p.status);
                const isAcarya = p.acaryaId || p.userId?.role === 'ACARYA';
                return (
                  <tr key={p._id} className="hover:bg-stone-50/50 transition-colors">
                    <td className="px-6 py-5">
                      <span className="font-mono text-xs text-stone-400 bg-stone-100 px-2 py-1 rounded">
                        {String(p._id).slice(-8).toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-5">
                      <div className="font-bold text-stone-800 text-sm">{p.userId?.name || 'User'}</div>
                      <div className="text-xs text-stone-400">{p.userId?.email || ''}</div>
                    </td>
                    <td className="px-6 py-5">
                      {isAcarya ? (
                        <span className="px-2 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200">
                          🔮 Acarya
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-purple-50 text-purple-700 border border-purple-200">
                          🏪 Vendor
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-5">
                      {p.vendorId ? (
                        <div className="text-[11px] text-stone-600 space-y-0.5">
                          <div className="font-bold text-stone-700">{p.vendorId.shopName || p.vendorId.legalEntityName || 'Vendor Profile'}</div>
                          <div><span className="text-stone-400">Holder:</span> {p.vendorId.bank?.accountHolder || '—'}</div>
                          <div><span className="text-stone-400">Bank:</span> {p.vendorId.bank?.bankName || '—'}</div>
                          <div><span className="text-stone-400">A/C No:</span> <span className="font-mono">{p.vendorId.bank?.accountNumber || '—'}</span></div>
                          <div><span className="text-stone-400">IFSC:</span> <span className="font-mono">{p.vendorId.bank?.ifscCode || '—'}</span></div>
                          <div><span className="text-stone-400">PAN:</span> <span className="font-mono">{p.vendorId.panNumber || '—'}</span></div>
                        </div>
                      ) : p.acaryaId ? (
                        <div className="text-[11px] text-stone-600 space-y-0.5">
                          <div className="font-bold text-amber-800">{p.acaryaId.name || 'Acarya Profile'} ({p.acaryaId.title})</div>
                          <div><span className="text-stone-400">A/C No:</span> <span className="font-mono">{p.acaryaId.payoutInfo?.bankAccount || '—'}</span></div>
                          <div><span className="text-stone-400">IFSC:</span> <span className="font-mono">{p.acaryaId.payoutInfo?.ifsc || '—'}</span></div>
                          <div><span className="text-stone-400">PAN:</span> <span className="font-mono">{p.acaryaId.payoutInfo?.pan || '—'}</span></div>
                        </div>
                      ) : (
                        <span className="text-xs text-stone-300 italic">No bank profile found</span>
                      )}
                    </td>
                    <td className="px-6 py-5">
                      <div className="text-sm text-stone-700 font-medium">
                        {new Date(p.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </div>
                      <div className="text-[10px] text-stone-400">
                        {new Date(p.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <span className="flex items-center gap-1.5 w-fit px-3 py-1 rounded-md text-[10px] font-black uppercase tracking-wider"
                        style={{ background: s.bg, color: s.color }}>
                        <span className="material-symbols-outlined text-[12px]">{s.icon}</span>
                        {p.status}
                      </span>
                    </td>
                    <td className="px-6 py-5 text-sm text-stone-400 italic">{p.note || '—'}</td>
                    <td className="px-6 py-5 text-right">
                      <span className="font-bold text-lg text-stone-800">₹{fmt(p.amount)}</span>
                    </td>
                    <td className="px-6 py-5">
                      {p.status === 'Pending' ? (
                        <div className="flex gap-2 justify-center">
                          <button
                            onClick={() => setNoteModal({ id: p._id, action: 'Success' })}
                            className="px-3 py-1.5 rounded-lg text-[11px] font-bold text-white transition-all hover:opacity-90"
                            style={{ background: '#22c55e' }}
                          >
                            ✓ Approve
                          </button>
                          <button
                            onClick={() => setNoteModal({ id: p._id, action: 'Failed' })}
                            className="px-3 py-1.5 rounded-lg text-[11px] font-bold text-white transition-all hover:opacity-90"
                            style={{ background: '#f87171' }}
                          >
                            ✗ Reject
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-stone-300 text-center block">—</span>
                      )}
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan="9" className="px-6 py-12 text-center">
                    <span className="material-symbols-outlined text-5xl block mb-3 text-stone-200">payments</span>
                    <p className="text-stone-400 font-bold">No payout requests found</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default VendorPayouts;
